import React from "react";
import { C } from "../theme";

// Collapsible "Conditions Timeline" for the Trip Report.
// Hand-rolled SVG (no chart lib) matching the app's other inline SVG charts.
// Plots barometric + wind as numeric lines, tide as a stepped ordinal band,
// and solunar as shaded windows. Catches are overlaid as numbered badges that
// mirror the Trip Report map (same color + sequence number) with species tooltips.

const W = 339; // internal viewBox width (~mobile frame content width)
const PAD_L = 8;
const PAD_R = 8;

// Tide stage stored as a label (not a numeric height) — map to an ordinal so it
// can be drawn as a stepped line. Unknown labels fall back to mid.
function tideLevel(stage) {
  const s = String(stage || "").toLowerCase();
  if (!s) return null;
  if (s.includes("high")) return 3;
  if (s.includes("ris") || s.includes("incom") || s.includes("flood")) return 2;
  if (s.includes("fall") || s.includes("outgo") || s.includes("ebb")) return 1;
  if (s.includes("low")) return 0;
  return 1.5;
}

// Solunar is stored as a label/type, not an intensity. Derive a coarse level so
// active feeding windows can be shaded. 0 none · 1 minor · 2 major.
function solunarLevel(sol) {
  if (!sol) return 0;
  const str = `${sol.type || ""} ${sol.label || ""}`.toLowerCase();
  if (str.includes("major")) return 2;
  if (str.includes("minor")) return 1;
  return sol.label || sol.type ? 1 : 0;
}

function formatTick(ms) {
  if (!Number.isFinite(ms)) return "";
  try {
    return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date(ms));
  } catch {
    return "";
  }
}

function CatchBadge({ x, y, sequence, color, title, onSelect, active }) {
  return (
    <g
      className="the99-catch-badge"
      style={{ cursor: "pointer" }}
      onClick={onSelect ? (e) => { e.stopPropagation(); onSelect(); } : undefined}
    >
      <title>{title}</title>
      {active && <circle cx={x} cy={y} r={12} fill="none" stroke={color} strokeWidth={2} opacity={0.8} />}
      <circle cx={x} cy={y} r={8} fill={color} stroke="#ffffff" strokeWidth={1.5} />
      <text
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={9}
        fontWeight={800}
        fill="#05101f"
        style={{ pointerEvents: "none" }}
      >
        {sequence}
      </text>
    </g>
  );
}

// A single numeric line chart (barometric / wind) with catch badges on the line.
function LineChart({ height, color, unit, points, catches, xOf, valueKey, onSelect, selectedId }) {
  const vals = [];
  points.forEach((p) => {
    if (Number.isFinite(p[valueKey])) vals.push(p[valueKey]);
  });
  catches.forEach((c) => {
    if (Number.isFinite(c[valueKey])) vals.push(c[valueKey]);
  });

  if (vals.length === 0) {
    return (
      <div style={{ fontSize: 10, color: C.muted, padding: "6px 4px 10px" }}>No {unit} data logged.</div>
    );
  }

  let min = Math.min(...vals);
  let max = Math.max(...vals);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const range = max - min;
  const padY = range * 0.14 || 1;
  const domMin = min - padY;
  const domMax = max + padY;
  const PAD_T = 8;
  const PAD_B = 8;
  const yOf = (v) => PAD_T + (1 - (v - domMin) / (domMax - domMin)) * (height - PAD_T - PAD_B);

  const linePts = points.filter((p) => Number.isFinite(p[valueKey]));
  const d = linePts
    .map((p, i) => `${i === 0 ? "M" : "L"}${xOf(p.atMs).toFixed(1)},${yOf(p[valueKey]).toFixed(1)}`)
    .join(" ");
  const area = linePts.length
    ? `${d} L${xOf(linePts[linePts.length - 1].atMs).toFixed(1)},${height} L${xOf(linePts[0].atMs).toFixed(1)},${height} Z`
    : "";

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" style={{ display: "block" }} aria-label={`${unit} over trip`}>
      {[0.25, 0.5, 0.75].map((f) => (
        <line key={f} x1={0} y1={height * f} x2={W} y2={height * f} stroke={C.border} strokeWidth={1} opacity={0.5} />
      ))}
      {area && <path d={area} fill={color} opacity={0.14} />}
      {linePts.length === 1 ? (
        <circle cx={xOf(linePts[0].atMs)} cy={yOf(linePts[0][valueKey])} r={3} fill={color} />
      ) : (
        <path d={d} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
      )}
      {catches.map((c) => {
        const cx = xOf(c.atMs);
        const cy = Number.isFinite(c[valueKey])
          ? yOf(c[valueKey])
          : height / 2;
        return (
          <g key={c.id}>
            <line x1={cx} y1={0} x2={cx} y2={height} stroke={c.color} strokeWidth={1} opacity={0.25} strokeDasharray="3 3" />
            <CatchBadge x={cx} y={cy} sequence={c.sequence} color={c.color} title={c.title} onSelect={() => onSelect?.(c)} active={selectedId === c.id} />
          </g>
        );
      })}
    </svg>
  );
}

function TideChart({ points, catches, xOf, onSelect, selectedId }) {
  const height = 72;
  const PAD_T = 12;
  const PAD_B = 12;
  const yOf = (lvl) => PAD_T + (1 - lvl / 3) * (height - PAD_T - PAD_B);
  const linePts = points
    .map((p) => ({ atMs: p.atMs, lvl: tideLevel(p.tideStage) }))
    .filter((p) => p.lvl != null);

  if (linePts.length === 0) {
    return <div style={{ fontSize: 10, color: C.muted, padding: "6px 4px 10px" }}>No tide data logged.</div>;
  }

  // Stepped line: hold each level until the next sample.
  let d = "";
  linePts.forEach((p, i) => {
    const x = xOf(p.atMs);
    const y = yOf(p.lvl);
    if (i === 0) {
      d += `M${x.toFixed(1)},${y.toFixed(1)}`;
    } else {
      const prevY = yOf(linePts[i - 1].lvl);
      d += ` L${x.toFixed(1)},${prevY.toFixed(1)} L${x.toFixed(1)},${y.toFixed(1)}`;
    }
  });

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" style={{ display: "block" }} aria-label="Tide stage over trip">
      <text x={2} y={yOf(3) + 3} fill={C.muted} fontSize={8}>High</text>
      <text x={2} y={yOf(1.5) + 3} fill={C.muted} fontSize={8}>Mid</text>
      <text x={2} y={yOf(0) + 3} fill={C.muted} fontSize={8}>Low</text>
      {[0, 1.5, 3].map((lvl) => (
        <line key={lvl} x1={30} y1={yOf(lvl)} x2={W} y2={yOf(lvl)} stroke={C.border} strokeWidth={1} opacity={0.4} />
      ))}
      <path d={d} fill="none" stroke={C.seafoam} strokeWidth={2.5} strokeLinejoin="round" />
      {catches.map((c) => {
        const lvl = tideLevel(c.tideStage);
        const cx = xOf(c.atMs);
        const cy = lvl != null ? yOf(lvl) : height / 2;
        return <CatchBadge key={c.id} x={cx} y={cy} sequence={c.sequence} color={c.color} title={c.title} onSelect={() => onSelect?.(c)} active={selectedId === c.id} />;
      })}
    </svg>
  );
}

function SolunarChart({ points, catches, xOf, onSelect, selectedId }) {
  const height = 44;
  const rowY = 10;
  const rowH = 24;
  const levels = points.map((p) => ({ atMs: p.atMs, lvl: solunarLevel(p.solunar) }));
  const anyActive = levels.some((l) => l.lvl > 0);

  // Band segments between sample midpoints, shaded by level.
  const segments = [];
  for (let i = 0; i < levels.length; i += 1) {
    if (levels[i].lvl <= 0) continue;
    const prevMid = i > 0 ? (levels[i - 1].atMs + levels[i].atMs) / 2 : levels[i].atMs;
    const nextMid = i < levels.length - 1 ? (levels[i].atMs + levels[i + 1].atMs) / 2 : levels[i].atMs;
    const x1 = xOf(prevMid);
    const x2 = xOf(nextMid);
    segments.push({ x: x1, w: Math.max(4, x2 - x1), opacity: levels[i].lvl >= 2 ? 0.55 : 0.28, key: i });
  }

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" style={{ display: "block" }} aria-label="Solunar periods over trip">
      <rect x={30} y={rowY} width={W - 30 - 4} height={rowH} rx={4} fill={C.deepWater} stroke={C.border} />
      {segments.map((s) => (
        <rect key={s.key} x={s.x} y={rowY} width={s.w} height={rowH} rx={3} fill="#7C5CFF" opacity={s.opacity} />
      ))}
      {!anyActive && (
        <text x={W / 2} y={rowY + rowH / 2 + 3} textAnchor="middle" fill={C.muted} fontSize={9}>
          No solunar windows during this trip
        </text>
      )}
      {catches.map((c) => (
        <CatchBadge key={c.id} x={xOf(c.atMs)} y={rowY + rowH / 2} sequence={c.sequence} color={c.color} title={c.title} onSelect={() => onSelect?.(c)} active={selectedId === c.id} />
      ))}
    </svg>
  );
}

function ChartRow({ label, value, children }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "8px 4px 2px" }}>
        <b style={{ color: C.white, fontSize: 12, fontWeight: 600 }}>{label}</b>
        {value && <span style={{ color: C.gold, fontSize: 11, fontVariantNumeric: "tabular-nums" }}>{value}</span>}
      </div>
      {children}
    </div>
  );
}

export default function SessionConditionsChart({ series = [], catches = [], startMs, endMs, defaultOpen = true }) {
  const [open, setOpen] = React.useState(defaultOpen);
  const [selected, setSelected] = React.useState(null);

  // Keep the selected-catch caption valid if the underlying catches change.
  React.useEffect(() => {
    if (selected && !catches.some((c) => c.id === selected.id)) setSelected(null);
  }, [catches, selected]);

  const points = React.useMemo(
    () => [...series].filter((p) => Number.isFinite(p?.atMs)).sort((a, b) => a.atMs - b.atMs),
    [series]
  );

  const hasData = points.length > 0 || catches.length > 0;

  const t0 = Number.isFinite(startMs) ? startMs : points[0]?.atMs;
  const t1 = Number.isFinite(endMs) ? endMs : points[points.length - 1]?.atMs;
  // Fall back to the catch time range when the condition series is sparse, so
  // the time axis still spans the trip even with few/no condition samples.
  const catchMsVals = React.useMemo(
    () => catches.map((c) => c.atMs).filter(Number.isFinite),
    [catches]
  );
  const axisMin = Number.isFinite(t0) ? t0 : (catchMsVals.length ? Math.min(...catchMsVals) : NaN);
  const axisMaxRaw = Number.isFinite(t1) ? t1 : (catchMsVals.length ? Math.max(...catchMsVals) : NaN);
  const axisMax = Number.isFinite(axisMaxRaw) && axisMaxRaw > axisMin ? axisMaxRaw : axisMin;
  const span = Number.isFinite(axisMin) && Number.isFinite(axisMax) && axisMax - axisMin > 0 ? axisMax - axisMin : 1;
  const xOf = React.useCallback(
    (ms) => {
      const base = Number.isFinite(axisMin) ? axisMin : 0;
      const frac = Math.max(0, Math.min(1, (ms - base) / span));
      return PAD_L + frac * (W - PAD_L - PAD_R);
    },
    [axisMin, span]
  );

  const baroLabel = React.useMemo(() => {
    const vals = points.map((p) => p.baro).filter(Number.isFinite);
    if (!vals.length) return "";
    const first = vals[0];
    const last = vals[vals.length - 1];
    const trend = last > first ? "rising" : last < first ? "falling" : "steady";
    return `${first.toFixed(2)} → ${last.toFixed(2)} inHg · ${trend}`;
  }, [points]);

  const windLabel = React.useMemo(() => {
    const vals = points.map((p) => p.wind).filter(Number.isFinite);
    if (!vals.length) return "";
    return `${Math.round(vals[0])} → ${Math.round(vals[vals.length - 1])} kn`;
  }, [points]);

  const axisTicks = React.useMemo(() => {
    if (!Number.isFinite(axisMin)) return [];
    return [0, 1 / 3, 2 / 3, 1].map((f) => {
      const ms = axisMin + f * span;
      return { ms, label: formatTick(ms), f };
    });
  }, [axisMin, span]);

  return (
    <div style={{ margin: "0 14px 12px" }}>
      <style>{`.the99-catch-badge{transform-box:fill-box;transform-origin:center;transition:transform .12s ease}.the99-catch-badge:hover{transform:scale(1.25)}`}</style>
      <div
        style={{
          background: C.cardBg,
          border: `1px solid ${C.border}`,
          borderRadius: 14,
          overflow: "hidden",
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "13px 14px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
          }}
          title={open ? "Hide conditions timeline" : "Show conditions timeline"}
        >
          <span style={{ flex: 1, fontSize: 10, color: C.muted, letterSpacing: 2 }}>
            CONDITIONS TIMELINE
            {catches.length > 0 && (
              <span style={{ letterSpacing: 0 }}>
                {" "}· {catches.length} fish
              </span>
            )}
          </span>
          <span style={{ color: C.muted, fontSize: 13, transform: open ? "none" : "rotate(-90deg)", transition: "transform .2s" }}>
            ▾
          </span>
        </button>

        {open && (
          <div style={{ padding: "0 12px 14px" }}>
            {!hasData ? (
              <div style={{ fontSize: 11, color: C.muted, padding: "4px 4px 10px" }}>
                No condition data was logged for this trip.
              </div>
            ) : (
              <>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 14px", padding: "2px 4px 8px" }}>
                  <Legend color={C.warning} label="Barometric" line />
                  <Legend color={C.gold} label="Wind" line />
                  <Legend color={C.seafoam} label="Tide" line />
                  <Legend color="#7C5CFF" label="Solunar" />
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: C.sand }}>
                    <i style={{ width: 10, height: 10, borderRadius: "50%", background: C.accent, border: "1.5px solid #fff", display: "inline-block" }} />
                    Fish caught (tap for species)
                  </span>
                </div>

                {selected && (
                  <div
                    onClick={() => setSelected(null)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      margin: "2px 4px 10px",
                      padding: "8px 10px",
                      background: C.deepWater,
                      border: `1px solid ${selected.color}`,
                      borderRadius: 8,
                      cursor: "pointer",
                    }}
                  >
                    <span style={{ width: 16, height: 16, borderRadius: "50%", background: selected.color, border: "1.5px solid #fff", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: "#05101f" }}>
                      {selected.sequence}
                    </span>
                    <span style={{ fontSize: 12, color: C.white, fontWeight: 600 }}>{selected.title}</span>
                    <span style={{ marginLeft: "auto", fontSize: 10, color: C.muted }}>tap to dismiss</span>
                  </div>
                )}

                <ChartRow label="Barometric" value={baroLabel}>
                  <LineChart height={96} color={C.warning} unit="pressure" points={points} catches={catches} xOf={xOf} valueKey="baro" onSelect={setSelected} selectedId={selected?.id} />
                </ChartRow>
                <Divider />
                <ChartRow label="Wind" value={windLabel}>
                  <LineChart height={80} color={C.gold} unit="wind" points={points} catches={catches} xOf={xOf} valueKey="wind" onSelect={setSelected} selectedId={selected?.id} />
                </ChartRow>
                <Divider />
                <ChartRow label="Tide stage">
                  <TideChart points={points} catches={catches} xOf={xOf} onSelect={setSelected} selectedId={selected?.id} />
                </ChartRow>
                <Divider />
                <ChartRow label="Solunar windows">
                  <SolunarChart points={points} catches={catches} xOf={xOf} onSelect={setSelected} selectedId={selected?.id} />
                </ChartRow>

                {axisTicks.length === 4 && (
                  <div style={{ marginTop: 2 }}>
                    <svg viewBox={`0 0 ${W} 20`} width="100%" style={{ display: "block" }} aria-label="Time axis">
                      <line x1={PAD_L} y1={1} x2={W - PAD_R} y2={1} stroke={C.border} strokeWidth={1} opacity={0.6} />
                      {axisTicks.map((tk, i) => {
                        const x = xOf(tk.ms);
                        const anchor = i === 0 ? "start" : i === axisTicks.length - 1 ? "end" : "middle";
                        const tx = i === 0 ? PAD_L : i === axisTicks.length - 1 ? W - PAD_R : x;
                        return (
                          <g key={i}>
                            <line x1={x} y1={1} x2={x} y2={5} stroke={C.muted} strokeWidth={1} />
                            <text x={tx} y={16} fontSize={9.5} fill={C.muted} textAnchor={anchor} style={{ fontVariantNumeric: "tabular-nums" }}>
                              {tk.label}
                            </text>
                          </g>
                        );
                      })}
                    </svg>
                    <div style={{ textAlign: "center", fontSize: 9, color: C.muted, letterSpacing: 1, marginTop: 1 }}>TIME OF DAY</div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Legend({ color, label, line }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: C.sand }}>
      <i
        style={
          line
            ? { width: 16, height: 3, borderRadius: 2, background: color, display: "inline-block" }
            : { width: 9, height: 9, borderRadius: "50%", background: color, display: "inline-block" }
        }
      />
      {label}
    </span>
  );
}

function Divider() {
  return <div style={{ height: 1, background: C.border, margin: "10px 2px", opacity: 0.6 }} />;
}
